import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { fetchJudgment, setJudgmentOrderDocument } from '@/lib/judgmentsApi';
import { downloadDocument, uploadDocument } from '@/lib/documentsApi';
import { useAuthStore } from '@/features/auth/store';
import {
  ShareNetwork as Share,
  CalendarBlank as Calendar,
  Buildings as Building2,
  Scales as Scale,
  Tag,
  Gavel,
  Globe,
  DownloadSimple,
  Paperclip,
  CircleNotch,
  Hash,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DOCUMENT_ACCEPT_ATTR } from '@jurivoxa/shared';

const WRITE_TYPES = ['admin', 'senior_clerk', 'silk', 'junior', 'door_tenant'];

export default function JudgmentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isClerk = user?.memberType === 'senior_clerk' || user?.memberType === 'junior_clerk';
  const isBarrister = ['silk', 'junior', 'pupil', 'door_tenant'].includes(user?.memberType ?? '');
  const basePath = isClerk ? '/clerk' : isBarrister ? '/barrister' : '/admin';
  const canWrite = WRITE_TYPES.includes(user?.memberType ?? '');
  const [uploadingOrder, setUploadingOrder] = useState(false);

  const { data: j, isLoading, error } = useQuery({
    queryKey: ['judgment', id],
    queryFn: () => fetchJudgment(id!),
    enabled: !!id,
  });

  const handleOrderUpload = async (file: File) => {
    if (!id || !j?.caseId) return;
    setUploadingOrder(true);
    try {
      const doc = await uploadDocument(file, { caseId: j.caseId, category: 'order' });
      await setJudgmentOrderDocument(id, doc.id);
      await queryClient.invalidateQueries({ queryKey: ['judgment', id] });
      toast.success('Court order attached');
    } catch (err: any) {
      toast.error(err?.response?.data?.error?.message ?? 'Could not attach court order');
    } finally {
      setUploadingOrder(false);
    }
  };

  if (isLoading) return (
    <div className="flex items-center justify-center py-24">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
    </div>
  );
  if (error || !j) return (
    <div className="py-24 text-center">
      <p className="text-sm text-muted-foreground">Judgment not found.</p>
      <button onClick={() => navigate(-1)} className="mt-4 text-xs text-brand-600 dark:text-brand-400 hover:underline">Back</button>
    </div>
  );

  return (
    <div>
      <PageHeader
        // Was `citation || id` — with no citation that printed the raw database id above the
        // title, a GUID that means nothing to a barrister. A neutral citation is deliberately
        // optional (most County Court matters produce an order, not a citeable judgment), so
        // when there is none the line is simply absent.
        eyebrow={j.citation || undefined}
        title={j.case}
        subtitle={`${j.court} · ${j.date}`}
        onBack={() => navigate(-1)}
        backLabel="Back to judgments"
        actions={
          <>
            {/* Only offered when there IS something to copy. The button was shown regardless, so
                on a judgment without a citation it put an empty string on the clipboard and
                still announced "Citation copied". */}
            {j.citation && (
              <Button
                variant="outline"
                size="md"
                leftIcon={<Share className="h-4 w-4" />}
                onClick={() => { navigator.clipboard.writeText(j.citation); toast.success('Citation copied'); }}
              >
                Copy citation
              </Button>
            )}
          </>
        }
      />

      {/* `min-w-0` on both columns: a grid item will not shrink below its content's minimum, and
          the attached order's filename is one unbroken string (a GUID), so its minimum was the
          WHOLE name — which held the column open at 629px inside a 390px phone and pushed the
          size and Download button off the screen entirely. */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main column */}
        <div className="min-w-0 space-y-6 lg:col-span-2">
          <Card>
            <CardContent className="p-6">
              <div className="flex flex-wrap items-center gap-3">
                <Badge variant={j.outcome === 'Won' ? 'success' : j.outcome === 'Lost' ? 'danger' : 'default'}>{j.outcome}</Badge>
                {j.area && <Badge>{j.area}</Badge>}
                {j.isPublic && <Badge variant="info"><Globe className="h-3 w-3" /> Public</Badge>}
              </div>
              {j.citation && <div className="mt-4 text-sm font-semibold text-brand-700 dark:text-brand-300">{j.citation}</div>}
              {j.judge && <div className="mt-1 text-sm text-muted-foreground">{j.judge}</div>}
            </CardContent>
          </Card>

          {j.summary && (
            <Card>
              <CardHeader><CardTitle>Summary</CardTitle></CardHeader>
              <CardContent>
                <p className="text-sm leading-relaxed text-foreground">{j.summary}</p>
              </CardContent>
            </Card>
          )}

          {j.keyPoints && (
            <Card>
              <CardHeader><CardTitle>Key points</CardTitle></CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-foreground">
                  {j.keyPoints.split('\n').filter(Boolean).map((p, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-brand-600" />
                      <span className="leading-relaxed">{p}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          )}

          {/* Court order — the sealed order document, or an action to attach it later. */}
          <Card>
            <CardHeader><CardTitle>Court order</CardTitle></CardHeader>
            <CardContent>
              {j.orderDocument ? (
                <div className="flex items-center justify-between gap-3 rounded-lg border border-border px-3 py-2.5">
                  <div className="flex min-w-0 items-center gap-2">
                    <Paperclip className="h-4 w-4 shrink-0 text-muted-foreground" />
                    <span className="truncate text-sm text-foreground">{j.orderDocument.name}</span>
                    {j.orderDocument.size && (
                      <span className="shrink-0 text-xs text-muted-foreground">{j.orderDocument.size}</span>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    leftIcon={<DownloadSimple className="h-4 w-4" />}
                    onClick={() => downloadDocument(j.orderDocument!.id, j.orderDocument!.name)}
                  >
                    Download
                  </Button>
                </div>
              ) : canWrite && j.caseId ? (
                <label className={`flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-border/70 px-3 py-4 text-sm text-muted-foreground transition hover:border-brand-300 hover:text-foreground ${uploadingOrder ? 'pointer-events-none opacity-60' : ''}`}>
                  {uploadingOrder ? <CircleNotch className="h-4 w-4 animate-spin" /> : <Paperclip className="h-4 w-4" />}
                  {uploadingOrder ? 'Uploading…' : 'Upload court order'}
                  <input
                    type="file"
                    accept={DOCUMENT_ACCEPT_ATTR}
                    className="hidden"
                    disabled={uploadingOrder}
                    onChange={(e) => { const f = e.target.files?.[0]; if (f) void handleOrderUpload(f); e.target.value = ''; }}
                  />
                </label>
              ) : (
                <p className="text-sm text-muted-foreground">No court order attached.</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Side column */}
        <div className="min-w-0 space-y-4">
          <Card>
            <CardHeader><CardTitle>Details</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              {j.court  && <SideRow icon={<Building2 className="h-4 w-4" />} label="Court"  value={j.court} />}
              {j.claimNumber && <SideRow icon={<Hash className="h-4 w-4" />} label="Claim number" value={j.claimNumber} mono />}
              {j.date   && <SideRow icon={<Calendar  className="h-4 w-4" />} label="Date"   value={j.date} mono />}
              {j.judge  && <SideRow icon={<Scale     className="h-4 w-4" />} label="Bench"  value={j.judge} />}
              {j.caseId && (
                <SideRow
                  icon={<Gavel className="h-4 w-4" />}
                  label="Matter"
                  value={
                    <Link to={`${basePath}/matters/${j.caseId}`} className="font-medium text-brand-600 dark:text-brand-400 hover:underline">
                      {j.case}
                    </Link>
                  }
                />
              )}
            </CardContent>
          </Card>

          {j.barrister && (
            <Card>
              <CardHeader><CardTitle>Counsel</CardTitle></CardHeader>
              <CardContent>
                <div className="flex items-center gap-2.5 rounded-md border border-border px-3 py-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-50 text-xs font-semibold uppercase text-brand-700 dark:text-brand-300">
                    {j.barrister.split(' ').map((p) => p[0]).join('').slice(0, 2)}
                  </div>
                  <div className="text-sm font-medium text-foreground">{j.barrister}</div>
                </div>
              </CardContent>
            </Card>
          )}

          {j.tags.length > 0 && (
            <Card>
              <CardHeader><CardTitle>Tags</CardTitle></CardHeader>
              <CardContent>
                <div className="flex flex-wrap gap-1.5">
                  {j.tags.map((t) => <Badge key={t}><Tag className="h-3 w-3" /> {t}</Badge>)}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}

function SideRow({
  icon,
  label,
  value,
  mono,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  mono?: boolean;
}) {
  return (
    <div className="flex items-start gap-2.5">
      <div className="mt-0.5 text-muted-foreground">{icon}</div>
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">{label}</div>
        <div className={`mt-0.5 ${mono ? 'text-xs' : 'text-sm'} text-foreground`}>{value}</div>
      </div>
    </div>
  );
}
